import Head from 'expo-router/head';

import { listSeedScript } from '@/components/detail/list-seed';
import { articleCities, articleImageAlt, articleTopicLabels } from '@/lib/i18n/article-content';
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from '@todo/core/i18n/locales';
import { urlForImage } from '@todo/core/sanity/client';
import type { ArticleDetail } from '@todo/core/sanity/queries';
import {
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  articlesCrumb,
  breadcrumbJsonLd,
  type HreflangLink,
  ogLocale,
  portableTextToPlain,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';

// Social-share crop of the main image (the 1.91:1 size Facebook/LinkedIn use).
const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

// Per-article <Head>, shared by the native and web variants of
// app/[lang]/articles/[slug]. When no document matches {locale, slug} (most
// often a locale whose translation doesn't exist yet — generateStaticParams
// unions slugs across all locale variants), render a noindex head instead of
// letting the root layout's English fallback title leak onto a non-English
// URL. `key` forces a fresh mount on locale/record change.
export function ArticleHead({
  article,
  locale,
  slug,
  notFoundTitle,
  relatedSeedKey,
}: {
  article: ArticleDetail | null;
  locale: Locale;
  slug: string | undefined;
  notFoundTitle: string;
  /** The articles list seed the page's "More articles" block reads (see ArticleRelated). */
  relatedSeedKey?: string;
}) {
  const prefetchKey = slug ? `${locale}:${slug}` : undefined;

  if (!article) {
    return (
      <Head key={prefetchKey}>
        <title>{`${notFoundTitle} — ${SITE_NAME}`}</title>
        <meta name="robots" content="noindex, follow" />
      </Head>
    );
  }

  const canonical = absoluteUrl(locale, `articles/${article.slug}`);
  const ogImage = article.mainImage
    ? urlForImage(article.mainImage).width(OG_IMAGE_WIDTH).height(OG_IMAGE_HEIGHT).url()
    : null;
  // Google recommends supplying the Article image in 16:9, 4:3 and 1:1 so any
  // surface can use it uncropped; Sanity crops each around the hotspot.
  const mainImage = article.mainImage;
  const structuredImages = mainImage
    ? [
        [1200, 675],
        [1200, 900],
        [1200, 1200],
      ].map(([w, h]) => urlForImage(mainImage).width(w).height(h).url())
    : [];
  const imageAlt = articleImageAlt(article);
  // The CMS `seoTitle` is the editor's SERP-sized title; seoTitle() still
  // guards the length (and adds the brand suffix only when it fits), since
  // the Studio only warns past 60 characters.
  const pageTitle = seoTitle(article.seoTitle?.trim() || article.title);
  const metaDescription = truncate(
    article.seoDescription?.trim() ||
      portableTextToPlain(article.body) ||
      `${article.title} — an article on ${SITE_NAME}.`,
  );
  const topicLabels = articleTopicLabels(article.topics, locale);
  const coveredCities = articleCities(article.cities);
  const keywords = [...topicLabels, ...coveredCities.map((c) => c.name)];
  // `datePublished` is required for Article rich results. These documents don't
  // all carry an explicit `publishedAt`, so fall back to Sanity's own creation
  // stamp rather than dropping the field.
  const datePublished = article.publishedAt ?? article._createdAt;
  const dateModified = article._updatedAt ?? datePublished;

  // Unlike events/tours, articles are separate per-language Sanity documents
  // with their own localized slug — a plain `hreflangLinks(path)` (same path,
  // swapped locale prefix) would point at a URL that doesn't exist. Build the
  // set from the document's own `translation.metadata` group instead, and
  // only emit it once a real sibling exists (a lone self-link isn't useful).
  const localized = [
    { locale, slug: article.slug },
    ...(article.translations ?? []).filter(
      (t): t is { locale: Locale; slug: string } => isSupportedLocale(t.locale) && t.locale !== locale,
    ),
  ];
  const defaultVariant = localized.find((l) => l.locale === DEFAULT_LOCALE);
  const hreflangSet: HreflangLink[] =
    localized.length > 1
      ? [
          ...localized.map((l) => ({
            hrefLang: l.locale,
            href: absoluteUrl(l.locale, `articles/${l.slug}`),
          })),
          ...(defaultVariant
            ? [{ hrefLang: 'x-default', href: absoluteUrl(defaultVariant.locale, `articles/${defaultVariant.slug}`) }]
            : []),
        ]
      : [];

  return (
    <Head key={prefetchKey}>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      {hreflangSet.map((link) => (
        <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
      ))}
      <meta property="og:type" content="article" />
      <meta property="og:locale" content={ogLocale(locale)} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {ogImage ? <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} /> : null}
      {ogImage ? <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} /> : null}
      {ogImage ? <meta property="og:image:alt" content={imageAlt} /> : null}
      {datePublished ? <meta property="article:published_time" content={datePublished} /> : null}
      {dateModified ? <meta property="article:modified_time" content={dateModified} /> : null}
      {topicLabels[0] ? <meta property="article:section" content={topicLabels[0]} /> : null}
      {keywords.map((tag) => (
        <meta key={`tag:${tag}`} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={metaDescription} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      {ogImage ? <meta name="twitter:image:alt" content={imageAlt} /> : null}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          url: canonical,
          mainEntityOfPage: canonical,
          inLanguage: locale,
          ...(structuredImages.length > 0 ? { image: structuredImages } : {}),
          ...(datePublished ? { datePublished } : {}),
          // `dateModified` from Sanity's own last-mutation stamp; falls back to
          // the publish date when the document has never been edited.
          ...(dateModified ? { dateModified } : {}),
          // `author` is required for Article rich results. These are staff
          // pieces with no per-article byline, so the site itself is the author.
          author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          publisher: { '@id': `${SITE_URL}/#organization` },
          ...(metaDescription ? { description: metaDescription } : {}),
          ...(topicLabels[0] ? { articleSection: topicLabels[0] } : {}),
          ...(keywords.length > 0 ? { keywords: keywords.join(', ') } : {}),
          // The cities the article covers, each tied to its city landing page
          // (every city in events-data is exported — see city-detail.ts).
          ...(coveredCities.length > 0
            ? {
                about: coveredCities.map((city) => ({
                  '@type': 'Place',
                  name: city.name,
                  url: absoluteUrl(locale, city.id),
                })),
              }
            : {}),
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbJsonLd(locale, [articlesCrumb(locale), { name: article.title }]))}
      </script>
      {/* Baked record so the first client render matches this static HTML and
          hydration doesn't mismatch (#418). */}
      <script id={prefetchElementId('article', prefetchKey ?? '')} type="application/json">
        {serializePrefetch(article)}
      </script>
      {/* Baked list seed for the "More articles" block, so its cards are in the
          static HTML and hydrate without a refetch. Emitted last so it never
          displaces the singular tags above. */}
      {relatedSeedKey ? listSeedScript(relatedSeedKey) : null}
    </Head>
  );
}
